<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import Action from '@/components/Action'
import Icon from '@/components/Icon'
// import logger from '@/logger/index'

const MENU_NAV_EXPANDED_KEY = 'tdrscherer-menu-nav-expanded'

function readMenuExpandedPreference () {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(MENU_NAV_EXPANDED_KEY)
    if (raw === null) return null
    return JSON.parse(raw) === true
  } catch {
    return null
  }
}

function persistMenuExpandedPreference (value) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(MENU_NAV_EXPANDED_KEY, JSON.stringify(!!value))
  } catch {
    // ignore quota / private mode
  }
}

export default {
  name: 'MenuNav',

  components: {
    Action,
    Icon,
  },

  data () {
    return {
      isExpanded: false,
      openSubmenus: {},
      /** Snapshot antes do recolhimento automático ao abrir filtros (apenas desktop) */
      menuExpandedBeforeFilters: null,
      /** Se o menu foi recolhido automaticamente ao abrir filtros (estava expandido) */
      didAutoCollapseForFilters: false,
    }
  },

  computed: {
    ...mapState(['menuOpenMobile']),
    ...mapGetters('auth', ['getUserName', 'getToken', 'getCNPJ']),
    ...mapGetters('userPermissions', ['getEnableParts']),

    menu () {
      return [
        {
          text: 'Produtos',
          icon: 'search',
          link: { name: 'products' },
          url: null,
          // submenu: [
          //   {
          //     text: 'Conj. Peças',
          //     link: { name: 'parts' },
          //     icon: 'sub-arrow-right',
          //   }
          // ],
        },
        {
          text: 'Pedidos',
          icon: 'clipboard-list',
          link: { name: 'orders' },
          url: null,
        },
        {
          text: 'Financeiro',
          icon: 'file-invoice-dollar',
          link: null,
          url: 'https://www2.scherer-sa.com.br/api/financeiro-login-token/' + this.getToken,
        },
        {
          text: 'Cadastro',
          icon: 'user-circle',
          link: null,
          url: null,
          submenu: [
            {
              text: 'Acrés. Financeiro',
              link: { name: 'profile.configPrice' },
              icon: 'sub-arrow-right',
            }
          ],
        }
      ]
    },

    computedClasses () {
      return {
        [this.$classes['menu']]: true,
        [this.$classes['is-expanded']]: this.isExpanded,
        [this.$classes['is-mobile']]: this.$media.mobile,
        [this.$classes['is-open']]: this.menuOpenMobile,
      }
    },

    expandedIcon () {
      return this.isExpanded ? 'chevron-left' : 'chevron-right'
    },

    expandedText () {
      return this.isExpanded ? 'Recolher' : ''
    },
  },

  watch: {
    $media () {
      if (this.$media.desktop) {
        const pref = readMenuExpandedPreference()
        this.isExpanded = pref === null ? true : pref
      } else {
        this.isExpanded = false
      }
    },

    // Watcher para abrir automaticamente o submenu quando navegar para uma rota de submenu
    $route: {
      immediate: true,
      handler (to) {
        this.autoOpenSubmenus(to)
        // Minimiza o menu em mobile/tablet ao navegar
        if (this.$media.mobile || this.$media.tablet) {
          this.isExpanded = false
        }
      },
    },
  },

  async mounted () {
    this.getPermissions({
      cnpj: this.getCNPJ,
    })
    if (this.$media.desktop) {
      const pref = readMenuExpandedPreference()
      this.isExpanded = pref === null ? true : pref
    } else {
      this.isExpanded = false
    }
    this.$root.$on('product-filters-open', this.onProductFiltersOpen)
    this.$root.$on('product-filters-close', this.onProductFiltersClose)
  },

  beforeDestroy () {
    this.$root.$off('product-filters-open', this.onProductFiltersOpen)
    this.$root.$off('product-filters-close', this.onProductFiltersClose)
  },

  methods: {
    ...mapActions(['setMenuOpen']),
    ...mapActions('userPermissions', ['getPermissions']),

    handleExpand () {
      this.isExpanded = !this.isExpanded
      // Só limpa os submenus quando o menu é recolhido
      if (!this.isExpanded) {
        this.openSubmenus = {}
      }
      if (this.$media.desktop) {
        persistMenuExpandedPreference(this.isExpanded)
      }
    },

    onProductFiltersOpen () {
      if (!this.$media.desktop) return
      this.menuExpandedBeforeFilters = this.isExpanded
      this.didAutoCollapseForFilters = this.isExpanded
      if (this.isExpanded) {
        this.isExpanded = false
        this.openSubmenus = {}
      }
    },

    onProductFiltersClose () {
      if (!this.$media.desktop) return
      if (this.didAutoCollapseForFilters) {
        this.isExpanded = this.menuExpandedBeforeFilters
      }
      this.menuExpandedBeforeFilters = null
      this.didAutoCollapseForFilters = false
    },

    handleOpenLink (link, index) {
      if (this.$media.mobile) this.setMenuOpen()

      if (!link) {
        this.toggleSubmenu(index)
        return
      }

      if (link.name === 'parts') {
        // Se já está na rota products com a query correta, apenas abre o modal
        if (this.$route.name === 'products' && this.$route.query.openModal === 'parts') {
          // Mantém o submenu aberto
          this.$set(this.openSubmenus, index, true)
          // Emite um evento global para abrir o modal
          this.$root.$emit('open-parts-modal')
          return
        }

        // Navega para products com a query para abrir o modal
        this.$router.replace({ name: 'products', query: { openModal: 'parts' } })
        // Mantém o submenu aberto
        this.$set(this.openSubmenus, index, true)
        return
      }

      // Para outros links, limpa os submenus
      this.openSubmenus = {}

      // Verifica se já está na rota atual, senão encaminha
      if (link && this.$route.name !== link.name) {
        this.$router.push(link)
      }
    },

    toggleSubmenu (index) {
      this.$set(
        this.openSubmenus,
        index,
        !this.openSubmenus[index]
      )
    },

    // Abre automaticamente os submenus baseado na rota atual
    autoOpenSubmenus (route) {
      if (!route) return

      // Se estiver na rota de parts ou produtos com modal de parts, abre o submenu de Produtos
      if (route.name === 'parts' || (route.name === 'products' && route.query.openModal === 'parts')) {
        this.$set(this.openSubmenus, 0, true)
      }

      // Se estiver na rota de profile.configPrice, abre o submenu de Cadastro
      if (route.name === 'profile.configPrice') {
        this.$set(this.openSubmenus, 3, true)
      }
    },

    isSubmenuActive (parentIndex, subIndex) {
      // Verifica se o submenu está ativo baseado na rota atual
      let isActive = false

      if (parentIndex === 0 && subIndex === 0) { // Conj. Peças
        isActive = this.$route.name === 'parts' || (this.$route.name === 'products' && this.$route.query.openModal === 'parts')
      } else if (parentIndex === 3 && subIndex === 0) { // Acrés. Financeiro
        isActive = this.$route.name === 'profile.configPrice'
      }

      return isActive
    },

    isMenuActive (index) {
      const currentRoute = this.$route.name
      const currentQuery = this.$route.query

      // Produtos
      if (index === 0) {
        const isActive = currentRoute === 'products' ||
          currentRoute === 'parts' ||
          (currentRoute === 'products' && currentQuery.openModal === 'parts')
        return isActive
      }

      // Pedidos
      if (index === 1) {
        const isActive = currentRoute === 'orders' || currentRoute === 'orders.details'
        return isActive
      }

      // Cadastro
      if (index === 3) {
        const isActive = currentRoute === 'profile.configPrice'
        return isActive
      }

      return false
    },
  },
}
</script>

<template>
  <aside :class="computedClasses">
    <div :class="$classes['menu-div']">
      <nav :class="$classes['menu-nav']">
        <!-- Mobile header -->
        <template v-if="$media.mobile">
          <action :class="$classes['menu-close']" @click="handleOpenLink">
            <icon name="times" :class="$classes['menu-close-icon']" />
          </action>
          <div :class="$classes['user']">
            <icon name="user-circle" :class="$classes['user-icon']" />
            <span :class="$classes['user-name']">{{ getUserName }}</span>
          </div>
        </template>

        <!-- Menu itens -->
        <div v-for="(menuItem, index) in menu" :key="index" :class="$classes['menu-item-wrapper']">
          <action :url="menuItem.url" :target="menuItem.url ? 'blank' : null" :class="{
            [$classes['menu-nav-link']]: true,
            [$classes['is-expanded']]: isExpanded,
            [$classes['is-active']]: isMenuActive(index)
          }" @click="handleOpenLink(menuItem.link, index)">
            <span :class="$classes['menu-nav-icon-wrapper']">
              <icon :name="menuItem.icon" :class="$classes['menu-nav-icon']" />
            </span>
            <div v-if="isExpanded || $media.mobile" :class="$classes['menu-div-content']">
              <span :class="$classes['menu-nav-text']">{{ menuItem.text }}</span>
              <span v-if="menuItem.submenu && getEnableParts"
                :class="[$classes['menu-nav-icon-arrow'], openSubmenus[index] ? $classes['arrow-up'] : $classes['arrow-down']]"
                @click.stop.prevent="toggleSubmenu(index)">
                <icon :name="openSubmenus[index] ? 'caret-up' : 'caret-down'" :class="$classes['menu-close-icon']" />
              </span>
            </div>
          </action>

          <!-- Submenu específico -->
          <ul v-if="menuItem.submenu && getEnableParts && (isExpanded || $media.mobile) && openSubmenus[index]"
            :class="$classes['submenu-list']">
            <li v-for="(sub, subIndex) in menuItem.submenu" :key="sub.text">
              <action :class="{
                [$classes['menu-nav-link']]: true,
                [$classes['is-active']]: isSubmenuActive(index, subIndex)
              }" @click="handleOpenLink(sub.link)">
                <span :class="$classes['submenu-nav-icon-wrapper']">
                  <icon :name="sub.icon" :class="$classes['menu-nav-icon']" />
                </span>
                <span :class="$classes['submenu-item']">{{ sub.text }}</span>
              </action>
            </li>
          </ul>
        </div>

        <!-- Logout (mobile) -->
        <action v-if="$media.mobile" :to="{ name: 'auth.logout' }" :class="$classes['menu-nav-link']">
          <span :class="$classes['menu-nav-icon-wrapper']">
            <icon name="sign-out-alt" :class="$classes['menu-nav-icon']" />
          </span>
          <span :class="$classes['menu-nav-text']">Sair da conta</span>
        </action>

        <!-- Toggle expand/recolher (desktop) -->
        <action v-if="!$media.mobile"
          :class="{ [$classes['menu-nav-link']]: true, [$classes['is-expanded']]: isExpanded }"
          :active-class="$classes['is-active']" @click="handleExpand(0)">
          <span :class="$classes['menu-nav-icon-wrapper']">
            <icon :name="expandedIcon" />
          </span>
          <div v-if="isExpanded || $media.mobile" :class="$classes['menu-div-content']">
            <span :class="$classes['menu-nav-text']">{{ expandedText }}</span>
          </div>
        </action>
      </nav>
    </div>
  </aside>
</template>

<style module>
.menu {
  background: #292929;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: flex-start;
  overflow: hidden;
  padding: 20px 0;
  transition: width 0.2s;
  width: 70px;
  will-change: width;
}

.menu.is-expanded {
  width: 210px;
  justify-content: flex-start;
}

.menu-div {
  position: fixed;
  left: 14px;
  width: auto;
  max-width: 196px;
}

.menu-nav {
  width: auto;
  margin: 0 auto;
  justify-content: center;
}

.menu-div-content {
  display: contents;
}

.menu-nav-link {
  justify-content: inherit;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  flex-shrink: 0;
  align-items: center;
  font-size: 14px;
  line-height: 18px;
  height: auto;
  color: #a1a1a1;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 5px;
  white-space: nowrap;
  transition: color 0.2s;
}

.menu-nav-link.is-expanded {
  justify-content: flex-start;
}

.menu-nav-link ul {
  padding-left: 15px;
  margin-bottom: 0;
  list-style: none;
  width: 100%;
}

.menu-nav-link ul li a {
  font-size: 12px;
  align-items: center;
}

.submenu-item {
  font-size: 12px;
  align-items: center;
}

.menu-nav-link:hover,
.menu-nav-link.is-active {
  color: #D14040;
}

/* Estilo específico para o menu ativo */
.menu-nav-link.is-active .menu-nav-icon {
  color: #D14040;
}

/* Estilo para o submenu ativo */
.submenu-list .menu-nav-link.is-active {
  color: #D14040;
}

.submenu-list .menu-nav-link.is-active .menu-nav-icon {
  color: #D14040;
}

.menu-nav-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  padding: 3px
}

.menu-nav-icon-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  padding: 3px
}

.menu-nav-icon-arrow.arrow-down:hover {
  background-color: #a1a1a127;
  border-radius: 5px;
  width: 30px;
  height: 30px;
}

.menu-item-wrapper {
  position: relative;
}

.submenu-list {
  padding-left: 15px;
  margin: 0 0 5px;
  list-style: none;
}

.submenu-nav-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 32px;
  flex-shrink: 0;
}

.submenu-nav-icon-wrapper .menu-nav-icon {
  height: 18px;
  width: 18px;
}

.menu-nav-text {
  margin-left: 15px;
  margin-right: 10px;
}

/* MOBILE */
@media (max-width: 767px) {
  .menu.is-mobile {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: auto;
    z-index: 99;
    transform: translateX(-100%);
    transition: transform 0.2s;
    background: rgba(0, 0, 0, 0.9);
    backdrop-filter: blur(10px);
    padding-top: 105px;
  }

  .menu.is-open {
    transform: translateX(0);
  }

  .menu-div {
    position: fixed;
    left: 14px;
    width: auto;
    display: contents;
    max-width: 210px;
  }

  .menu-nav {
    width: 230px;
    margin: 0 auto;
  }

  .menu-close {
    position: absolute;
    top: 30px;
    left: 20px;
    color: rgba(255, 255, 255, 0.54);
  }

  .menu-close-icon {
    width: 20px;
    height: 20px;
  }

  .user {
    width: 230px;
    margin-bottom: 25px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .user-icon {
    width: 66px;
    height: 66px;
    color: #fff;
  }

  .user-name {
    display: block;
    margin-top: 24px;
    color: #fff;
    font-size: 14px;
    letter-spacing: 0.1em;
  }

  .menu-nav-link {
    font-size: 18px;
    padding-left: 0;
  }

  .menu-nav-link ul li a {
    font-size: 14px;
    align-items: center;
  }

  .menu-nav-icon-wrapper .menu-nav-icon {
    height: 20px;
    width: 20px;
  }

  .menu-nav-text {
    margin-left: 20px;
  }
}
</style>
